import { useEffect, useRef, useState } from "react";

// Define the options type for IntersectionObserver
interface IntersectionObserverOptions extends IntersectionObserverInit {}

// Define the return type for the hook
interface IntersectionObserverEntryWithRef extends IntersectionObserverEntry {
  target: Element;
}

// Hook definition
const useIntersectionObserver = (options?: IntersectionObserverOptions): [
  (node: Element | null) => void,
  IntersectionObserverEntryWithRef | null
] => {
  const [entry, setEntry] = useState<IntersectionObserverEntryWithRef | null>(
    null
  );
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Set up observer
  const observe = (node: Element | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect(); // Clean up previous observer
    }

    if (node) {
      observerRef.current = new IntersectionObserver(([newEntry]) => {
        setEntry(newEntry as IntersectionObserverEntryWithRef);
      }, options);
      observerRef.current.observe(node);
    }
  };

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect(); // Cleanup observer on unmount
    };
  }, []);

  return [observe, entry];
};

export default useIntersectionObserver;
